// Copyright (c) 2026 Query Health Inc. All rights reserved.
// Author: Anmol Rajpal <anmol@quantinohealth.com>
// Licensed under a Commercial License. See LICENSE for terms.

// Access-control helpers for console routes. Pure functions — no React, no side
// effects — so they can be exhaustively unit-tested.

import type { Role } from '@/types'

export type ConsoleRoute =
  | '/orgs'
  | '/orgs/new'
  | '/orgs/:orgId'
  | '/usage'
  | '/team'
  | '/settings'

// Numeric rank assigned to each role. Roles with rank < 2 are not operators
// and cannot access any console route.
export const RANK: Record<Role, number> = {
  platform_superadmin: 3,
  qh_admin: 2,
  org_admin: 0,
  clinic_staff: 0,
  provider: 0,
}

// Minimum rank required to access each console route.
const ROUTE_MIN_RANK: Record<ConsoleRoute, number> = {
  '/orgs': 2,
  '/orgs/new': 2,
  '/orgs/:orgId': 2,
  '/usage': 2,
  '/team': 2,
  '/settings': 3,
}

/** Returns true iff the given role is allowed to access the console route. */
export function canAccess(role: Role | null, route: ConsoleRoute): boolean {
  if (!role) return false
  return RANK[role] >= ROUTE_MIN_RANK[route]
}

/**
 * Returns true iff the role meets the minimum operator threshold (qh_admin+).
 * Used by RequireRole to decide whether to redirect to /orgs or show AccessDenied.
 */
export const isOperator = (role: Role | null): boolean =>
  !!role && RANK[role] >= 2

// Roles a caller may invite or assign, mirroring the backend grant policy. The
// console manages the operator org exclusively, so only operator roles appear.
// UX-gating only; the backend is the real gate (returns 403 forbidden_role).
export function grantableRoles(caller: Role | null): Role[] {
  if (caller === 'platform_superadmin') return ['platform_superadmin', 'qh_admin']
  if (caller === 'qh_admin') return ['qh_admin']
  return []
}

/** True iff caller may invite/change/remove a member currently holding target. */
export const canGrantRole = (caller: Role | null, target: Role): boolean =>
  grantableRoles(caller).includes(target)
